import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  getCurrencySettings,
  listCoreCurrencies,
  setPrimaryCurrency,
  SYMBOL_POSITIONS,
  updateCoreCurrency,
  type CoreCurrency,
} from '@/lib/coreSystem';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function CurrenciesTab({ org }: Props) {
  const [currencies, setCurrencies] = useState<CoreCurrency[]>([]);
  const [primary, setPrimary] = useState('EUR');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [savingPrimary, setSavingPrimary] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, settings] = await Promise.all([
        listCoreCurrencies(),
        getCurrencySettings(org.id),
      ]);
      setCurrencies(list);
      setPrimary(settings.primary_currency_code || 'EUR');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const changePrimary = async (code: string) => {
    setPrimary(code);
    setSavingPrimary(true);
    try { await setPrimaryCurrency(org.id, code); } finally { setSavingPrimary(false); }
  };

  const patchCurrency = async (c: CoreCurrency, patch: Parameters<typeof updateCoreCurrency>[1]) => {
    setBusyId(c.id);
    try {
      await updateCoreCurrency(c.id, patch);
      setCurrencies((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...mapPatch(patch) } : x)));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = currencies.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return c.code.toLowerCase().includes(q) || (c.nom || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <p className="text-xs text-foreground-500 mb-4 max-w-2xl">
        Zifek supporte plusieurs devises. Votre entreprise possède une <span className="font-semibold text-foreground-700">devise principale</span> ;
        les modules peuvent en utiliser d'autres quand nécessaire, sans jamais les mélanger implicitement.
      </p>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-xl"></i></span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Devise principale</p>
              <p className="text-xs text-foreground-500">Celle utilisée par défaut dans tous vos modules.</p>
            </div>
          </div>
          <select
            value={primary}
            onChange={(e) => changePrimary(e.target.value)}
            disabled={savingPrimary}
            className="w-full sm:w-52 px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer disabled:opacity-60"
          >
            {currencies.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.nom}</option>)}
          </select>
        </div>
      </div>

      <div className="relative mb-3 max-w-md">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher une devise…"
          className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
        />
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-background-100 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
            <span className="col-span-3">Devise</span>
            <span className="col-span-2">Symbole</span>
            <span className="col-span-2">Précision</span>
            <span className="col-span-3">Taux de change</span>
            <span className="col-span-2 text-right">Active</span>
          </div>
          {filtered.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-background-200/60 items-center">
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-foreground-900">{c.code}</span>
                <span className="text-xs text-foreground-500 truncate">{c.nom}</span>
                {c.code === primary && <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">principale</span>}
              </div>
              <div className="col-span-2 flex items-center gap-1">
                <input
                  className="w-14 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 text-center focus:outline-none focus:ring-2 focus:ring-primary-300"
                  defaultValue={c.symbole || ''}
                  onBlur={(e) => { if (e.target.value !== (c.symbole || '')) patchCurrency(c, { symbole: e.target.value }); }}
                />
                <select
                  className="px-1.5 py-1 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-700 focus:outline-none cursor-pointer"
                  value={c.symbol_position || 'before'}
                  onChange={(e) => patchCurrency(c, { symbol_position: e.target.value })}
                >
                  {SYMBOL_POSITIONS.map((p) => <option key={p.key} value={p.key}>{p.key === 'before' ? 'Av.' : 'Ap.'}</option>)}
                </select>
              </div>
              <input
                type="number" min={0} max={4}
                className="col-span-2 w-16 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                defaultValue={c.precision ?? 2}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== (c.precision ?? 2)) patchCurrency(c, { precision: v }); }}
              />
              <input
                type="number" step="0.0001" min={0}
                className="col-span-3 w-28 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-300"
                defaultValue={c.exchange_rate ?? 1}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== (c.exchange_rate ?? 1)) patchCurrency(c, { exchange_rate: v }); }}
              />
              <div className="col-span-2 flex justify-end">
                <button
                  onClick={() => patchCurrency(c, { is_active: c.is_active ? 0 : 1 })}
                  disabled={busyId === c.id}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${c.is_active ? 'bg-primary-500' : 'bg-background-200'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${c.is_active ? 'left-[22px]' : 'left-0.5'}`}></span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function mapPatch(patch: Parameters<typeof updateCoreCurrency>[1]): Partial<CoreCurrency> {
  const out: Partial<CoreCurrency> = {};
  if (patch.is_active !== undefined) out.is_active = patch.is_active;
  if (patch.precision !== undefined) out.precision = patch.precision;
  if (patch.symbol_position !== undefined) out.symbol_position = patch.symbol_position;
  if (patch.exchange_rate !== undefined) out.exchange_rate = patch.exchange_rate;
  if (patch.symbole !== undefined) out.symbole = patch.symbole;
  if (patch.nom !== undefined) out.nom = patch.nom;
  return out;
}